import axios from "axios";
import { API_ROOT } from ".";
import { getAuthUser } from "./auth";
import type {
  CartItem,
  DeliveryMethod,
} from "../components/dashboard/utils/types";

const ORDERS_API_URL = `${API_ROOT}/orders`;

function getAuthHeaders(): { Authorization: string } {
  const token = getAuthUser()?.token;
  if (!token) throw new Error("Not authenticated");
  return { Authorization: `Bearer ${token}` };
}

export interface Order {
  id: string;
  businessId: string;
  businessName: string;
  companyRegistrationNumber: string;
  phone: string;
  email: string;
  totalPrice: number;
  deliveryMethod: DeliveryMethod;
  /** Company address at time of order (for shipping). */
  companyAddress?: string;
  companyPostalCode?: string;
  companyCity?: string;
  /** Shipping price in kr at time of order (0 for pickup). */
  shippingPrice?: number;
  status: string;
  products: CartItem[];
  createdAt: string;
  updatedAt: string;
}

export type OrderInventoryRestore = {
  restored: string[];
  skipped: string[];
};

export type OrderUpdateResponse = Order & {
  inventoryRestore?: OrderInventoryRestore;
};

/** Backend stores only companyRegistrationNumber on the order; business details come from User lookup when fetching. */
export interface CreateOrderPayload {
  companyRegistrationNumber: string;
  totalPrice: number;
  deliveryMethod: DeliveryMethod;
  products: CartItem[];
}

export async function callFetchOrders(): Promise<Order[]> {
  const token = getAuthUser()?.token;
  if (!token) return [];
  const res = await axios.get<Order[]>(ORDERS_API_URL, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return Array.isArray(res.data) ? res.data : [];
}

export async function callFetchOrder(id: string): Promise<Order> {
  const res = await axios.get<Order>(`${ORDERS_API_URL}/${id}`, {
    headers: getAuthHeaders(),
  });
  return res.data;
}

export async function callCreateOrder(
  data: CreateOrderPayload,
): Promise<Order> {
  const res = await axios.post<Order>(ORDERS_API_URL, data, {
    headers: getAuthHeaders(),
  });
  return res.data;
}

export async function callUpdateOrderStatus(
  id: string,
  status: string,
): Promise<OrderUpdateResponse> {
  const res = await axios.put<OrderUpdateResponse>(
    `${ORDERS_API_URL}/${id}`,
    { status },
    { headers: getAuthHeaders() },
  );
  return res.data;
}

export function getOrderYear(createdAt: string): number | null {
  const created = new Date(createdAt);
  if (Number.isNaN(created.getTime())) return null;
  return created.getFullYear();
}

export function isOrderInYear(createdAt: string, year: number): boolean {
  return getOrderYear(createdAt) === year;
}
